import { parseISO } from 'date-fns';
import { api } from 'domains/api';
import { BASE_ANONYMOUS_API_ROUTES, getBaseAPIRoute } from 'domains/api/base/routes';
import { parseTimeSlotsData } from 'domains/calendars/dataNormalization';
import {
  AvailableTimes,
  RawAvailableTimes,
  RawScheduleAvailableTimes,
  ScheduleAvailableTimes,
  RawAvailabilityResponse,
  EvaluationTimeSlotSummary,
  EvaluationTimeSlotSummaryRaw,
  EvaluationDuration,
} from 'domains/calendars/types';
import { Therapist } from 'domains/therapists/types';
import { parseInt, sortBy } from 'lodash';
import { StateAbbrev, toStateFull } from 'utils/states';

type BaseTimeSlotFilters = Partial<{
  insuranceName: string;
  state: string;
  specialties: string;
}>;

type ConsultationAvailableTimesParameters = {
  appointmentType: string;
  calendarID?: string;
};

export const getConsultationAvailableTimes = (params: ConsultationAvailableTimesParameters): Promise<AvailableTimes> =>
  api.baseAnonymous
    .get<RawAvailableTimes>(getBaseAPIRoute(BASE_ANONYMOUS_API_ROUTES.appointments.consultations['available-times']), {
      params,
    })
    .then(parseTimeSlotsData);

type RescheduleAvailableTimesParameters = {
  clientID: string;
  appointmentID: number;
  /** ISO date string for the first day to fetch, onwards */
  date: string;
};

/** Fetches available times for a reschedule starting from a given `startDate` */
export const getRescheduleAvailableTimes = (
  clientId: string,
  appointmentId: number,
  startDate: Date,
): Promise<AvailableTimes> => {
  return api.baseAnonymous
    .get<Record<string, string[]>>(
      getBaseAPIRoute(BASE_ANONYMOUS_API_ROUTES.appointments['rescheduling-available-times']),
      {
        params: {
          clientID: clientId,
          appointmentID: appointmentId,
          date: startDate.toISOString(),
        } as RescheduleAvailableTimesParameters,
      },
    )
    .then(parseTimeSlotsData);
};

type PendingRescheduleAvailableTimesParameters = {
  clientID: string;
  pendingRescheduleID: string;
  /** ISO date string for the first day to fetch, onwards */
  date: string;
};

/** Fetches available times for a pending reschedule starting from a given `startDate` */
export const getPendingRescheduleAvailableTimes = (
  clientId: string,
  pendingRescheduleId: string,
  startDate: Date,
): Promise<AvailableTimes> => {
  return api.baseAnonymous
    .get<Record<string, string[]>>(
      getBaseAPIRoute(BASE_ANONYMOUS_API_ROUTES.appointments['rescheduling-available-times']),
      {
        params: {
          clientID: clientId,
          pendingRescheduleID: pendingRescheduleId,
          date: startDate.toISOString(),
        } as PendingRescheduleAvailableTimesParameters,
      },
    )
    .then(parseTimeSlotsData);
};

export const getScheduleAvailableTimes = (
  date: Date,
  clientId: string,
  pendingScheduleId: string,
): Promise<ScheduleAvailableTimes> => {
  return api.baseAnonymous
    .get<RawScheduleAvailableTimes>(
      getBaseAPIRoute(BASE_ANONYMOUS_API_ROUTES.appointments['scheduling-available-times']),
      {
        params: {
          date: date.toISOString(),
          clientID: clientId,
          pendingScheduleID: pendingScheduleId,
        },
      },
    )
    .then((rawData: RawScheduleAvailableTimes): ScheduleAvailableTimes => {
      const key = date.toISOString().split('T')[0];
      const calendars = rawData[key];
      const availableTimes: { calendarID: number; time: Date; therapistName: string }[] = [];

      if (calendars) {
        calendars.forEach(calendar => {
          calendar.availableTimes.forEach(time => {
            availableTimes.push({
              therapistName: calendar.therapistName,
              calendarID: parseInt(calendar.calendarID),
              time: new Date(time),
            });
          });
        });
      }

      return {
        day: date,
        availableTimes: sortBy(availableTimes, time => time.time.getTime()),
      };
    });
};

export const getTherapists = async ({ state, insuranceName, specialties }: BaseTimeSlotFilters): Promise<Therapist[]> =>
  api.baseAnonymous.get<Therapist[]>(getBaseAPIRoute(BASE_ANONYMOUS_API_ROUTES.therapists['signup']), {
    params: { state: toStateFull(state as StateAbbrev), insuranceName, specialties },
  });

export interface EvaluationTimesSummaryParams extends BaseTimeSlotFilters {
  fromDate: string;
  toDate: string;
  duration: EvaluationDuration;
  therapistID?: string;
  primaryTherapistID?: string;
  appointmentID?: string;
  clientID?: string;
  acceptReassignments?: boolean;
}

export interface AvailabilityTimesSummaryParams extends BaseTimeSlotFilters {
  fromDate: string;
  toDate: string;
  duration: EvaluationDuration;
  appointmentTypeID?: number;
  therapistID?: string;
  insuranceName?: string;
}

export const getEvaluationTimesSummary = async ({
  fromDate,
  insuranceName,
  specialties,
  state,
  toDate,
  duration,
  clientID,
  appointmentID,
  therapistID,
  primaryTherapistID,
  acceptReassignments,
}: EvaluationTimesSummaryParams): Promise<EvaluationTimeSlotSummary> => {
  return api.baseAnonymous
    .get<EvaluationTimeSlotSummaryRaw>(
      getBaseAPIRoute(BASE_ANONYMOUS_API_ROUTES.appointments['therapists-availability']),
      {
        params: {
          fromDate,
          insuranceName,
          specialties,
          state: toStateFull(state as StateAbbrev),
          toDate,
          duration,
          clientID,
          appointmentID,
          therapistID,
          primaryTherapistID,
          acceptReassignments,
        },
      },
    )
    .then(d => ({
      therapists: d.therapists,
      times: d.times.map(({ time, ...rest }) => ({
        ...rest,
        time: parseISO(time),
      })),
    }));
};

export const getAvailabilityTimesSummary = async ({
  fromDate,
  insuranceName,
  specialties,
  state,
  toDate,
  duration,
  appointmentTypeID,
  therapistID,
}: AvailabilityTimesSummaryParams): Promise<EvaluationTimeSlotSummary> =>
  api.baseAnonymous
    .get<EvaluationTimeSlotSummaryRaw>(
      getBaseAPIRoute(BASE_ANONYMOUS_API_ROUTES.appointments['therapists-availability']),
      {
        params: { fromDate, insuranceName, specialties, state, toDate, duration, appointmentTypeID, therapistID },
      },
    )
    .then(d => ({
      therapists: d.therapists,
      times: d.times.map(({ time, therapistEmails }) => ({
        time: parseISO(time),
        therapistEmails,
      })),
    }));

export const getScheduleAvailableTimesV3 = (
  date: Date,
  state: string,
  signal?: AbortSignal,
): Promise<ScheduleAvailableTimes> => {
  return api.baseAnonymous
    .get<RawScheduleAvailableTimes>(
      getBaseAPIRoute(BASE_ANONYMOUS_API_ROUTES.appointments.v3['scheduling-available-times']),
      {
        params: {
          date: date.toISOString(),
          state: toStateFull(state as StateAbbrev),
        },
        signal,
      },
    )
    .then((rawData: RawScheduleAvailableTimes): ScheduleAvailableTimes => {
      const key = date.toISOString().split('T')[0];
      const calendars = rawData[key];
      const availableTimes: { calendarID: number; time: Date; therapistName: string }[] = [];

      if (calendars) {
        calendars.forEach(calendar => {
          calendar.availableTimes.forEach(time => {
            availableTimes.push({
              therapistName: calendar.therapistName,
              calendarID: parseInt(calendar.calendarID),
              time: new Date(time),
            });
          });
        });
      }

      return {
        day: date,
        availableTimes: sortBy(availableTimes, time => time.time.getTime()),
      };
    });
};

interface ScheduleAvailableTimesV4Params {
  date: Date;
  insuranceName: string | undefined;
  signal?: AbortSignal;
  state: string;
  specialties: string;
  prewarm?: boolean;
  therapistEmail?: string;
}

// @TODO BETTER NAMES PLEASE
interface ScheduleAvailableTimesV4ParamsNew {
  date: Date;
  therapist: Therapist;
}

interface CalendarTimeSlot {
  therapistName: string;
  calendarID: number;
  time: Date;
}

interface RawScheduleAvailableTimesV4 {
  calendarID: string;
  availableTimes: string[];
}

export const getScheduleAvailableTimesV4 = ({
  date,
  insuranceName,
  state,
  specialties,
  signal,
  prewarm,
}: ScheduleAvailableTimesV4Params): Promise<ScheduleAvailableTimes> => {
  return api.baseAnonymous
    .get<RawScheduleAvailableTimes>(
      getBaseAPIRoute(BASE_ANONYMOUS_API_ROUTES.appointments.v3['scheduling-available-times']),
      {
        params: {
          date: date.toISOString(),
          insuranceName,
          state: toStateFull(state as StateAbbrev),
          specialties,
          prewarm,
        },
        signal,
      },
    )
    .then((rawData: RawScheduleAvailableTimes): ScheduleAvailableTimes => {
      const key = date.toISOString().split('T')[0];
      const calendars = rawData[key];
      const availableTimes: { calendarID: number; time: Date; therapistName: string }[] = [];

      if (calendars) {
        calendars.forEach(calendar => {
          calendar.availableTimes.forEach(time => {
            availableTimes.push({
              therapistName: calendar.therapistName,
              calendarID: parseInt(calendar.calendarID),
              time: new Date(time),
            });
          });
        });
      }

      return {
        day: date,
        availableTimes: sortBy(availableTimes, time => time.time.getTime()),
      };
    });
};

export const getScheduleAvailableTimesV4New = ({
  date,
  therapist,
}: ScheduleAvailableTimesV4ParamsNew): Promise<ScheduleAvailableTimes> => {
  return api.baseAnonymous
    .get<RawScheduleAvailableTimesV4>(
      getBaseAPIRoute(BASE_ANONYMOUS_API_ROUTES.appointments.v4['scheduling-available-times']),
      {
        params: {
          date: date.toISOString(),
          calendarID: therapist.acuityCalendarID,
        },
      },
    )
    .then((rawData: RawScheduleAvailableTimesV4): ScheduleAvailableTimes => {
      const availableTimes: CalendarTimeSlot[] = (rawData.availableTimes || []).map((time: string) => {
        return {
          therapistName: `${therapist.firstName} ${therapist.lastName}`, //@TODO(juan): might as well return the entire therapist
          calendarID: parseInt(rawData.calendarID, 10),
          time: parseISO(time),
        };
      });

      return {
        day: date,
        availableTimes,
      };
    });
};

interface ICalendarAvailability {
  calendarID: string;
  date: string;
}

export const getCalendarAvailability = ({
  calendarID,
  date,
}: ICalendarAvailability): Promise<RawAvailabilityResponse> => {
  return api.baseAnonymous.get<RawAvailabilityResponse>(
    getBaseAPIRoute(BASE_ANONYMOUS_API_ROUTES.appointments['calendar-availability']),
    {
      params: {
        calendarID,
        date,
      },
    },
  );
};
